<script type="ts">
  import { t, locale, findKeyByValue } from "../translations/i18n";
  import translations from "../translations/translation";
  import MdLanguage from 'svelte-icons/md/MdLanguage.svelte';
  import { tooltip } from "svooltip";
  import 'svooltip/styles.css';
  import { generateUID } from "../data-views/utils";
  import { tick } from "svelte";
  export let title: string = "";
  export let state: { [key: string]: string | string[] } = {};
  export let key: string = "";
  export let options: { [key: string]: string };
  export let handler: (selected: string | string[]) => void;
  export let disabled: boolean = false;
  export let customPeriod: boolean = false;

  let dropdownOpen: boolean = false;
  let currentIndex = -1;
  let uniqueId: string = generateUID();

  const closeDropdown = (): void => {
    dropdownOpen = false;
  };

  const toggleDropdownHandler = (event: Event): void => {
    event.stopPropagation();
    if (disabled) return;

    const opening = !dropdownOpen;
    document.body.click(); // click "outside" to close dropdowns
    if (opening) {
      dropdownOpen = true;
    }
  };

  const chooseOptionHandler = (event: Event): void => {

    event.stopPropagation();

    customPeriod = false;

    const element = event.target as HTMLElement;
    let value = element.textContent ? element.textContent.trim() : '';
    value = findKeyByValue(translations[$locale], value);

    if (value !== state[key]) {
      if (key !== "") {
        state[key] = value;
      }

      handler(value);
    }

    dropdownOpen = false;
  };

  const customPeriodHandler = (event: Event): void => {
    event.stopPropagation();
    customPeriod = true;

    dropdownOpen = false;
  };

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = async (event: KeyboardEvent): Promise<void> => {
    if (!dropdownOpen) return;

    let optionsLength = Object.keys(options).length;
    switch (event.key) {
      case "Escape":
        closeDropdown();
        break;

      case "Enter":
        if (currentIndex !== -1) {
          // At first, we need to find dropdown with it's id, then we can find the value of the selected option
          const dropdown = document.getElementById(`icon-dropdown-${uniqueId}`);
          let selectionOption = dropdown?.querySelectorAll('li')[currentIndex];
          if (selectionOption) {
            selectionOption.click();
          }

          // Set the focus to the new visualization, when you create a new visualization 
          if (dropdown?.parentElement?.id === "add-visualization-container") {
            await tick();
            const visualisations = document.querySelector("main .visualizations");
            const visualizationList = visualisations?.querySelectorAll('[id^="container-id-"]');
            if (visualizationList && visualizationList.length > 0) {
              (visualizationList[visualizationList.length - 1] as HTMLElement)?.focus();
            }
          }
          
          // This is a workaround to focus on the visualization container after selecting an option in the visualization header.
          let parentVisualizationID = dropdown?.parentElement?.id.replace("headerButtons-", "");
          if (parentVisualizationID) {
            let parentVisualization = document.getElementById("container-id-" + parentVisualizationID);
            parentVisualization?.focus();
          }
        }
        break;

      case "ArrowDown":
        currentIndex = (currentIndex + 1) % optionsLength;
        focusCurrentOption();
        break;

      case "ArrowUp":
        currentIndex = (currentIndex - 1 + optionsLength) % optionsLength;
        focusCurrentOption();
        break;

      case "Tab":
        if (!dropdownOpen) return;
        currentIndex = (currentIndex + 1) % (optionsLength + 1);
      if (currentIndex === optionsLength) {
        focusCurrentOption();
        currentIndex = -1;
      } else {
        focusCurrentOption();
      }
      break;

      default:
        // Allow default behavior for other keys
        return;
    }

    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };

  /**
   * Focus the current view
   * @returns void
  */
  const focusCurrentOption = (): void => {
    const dropdown = document.getElementById(`icon-dropdown-${uniqueId}`);
    const options = dropdown?.querySelectorAll('li');
    if (options && options[currentIndex]) {
      options[currentIndex].focus();
      options[currentIndex].scrollIntoView({ block: "nearest" });
    }
  };
</script>

<svelte:window on:click={closeDropdown} />
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div 
  class="dropdown" 
  class:disabled 
  on:click={toggleDropdownHandler} {title} 
  data-testid="icon-dropdown-test"  
  id="icon-dropdown-{uniqueId}" 
  on:keydown={onKeyDown} 
  tabindex="0"
  use:tooltip={{
    content: {title},
    placement: 'top',
    delay: [250, 0],
    offset: 15,
    html: true,
    target: 'div.dropdown',
  }}
>
  <slot name="icon" />
  {#if dropdownOpen}
    <ul>
      {#if title != "Add visualization"}
        <p class="dropdown-title">{title}</p>
      {/if}
      {#each Object.values(options) as option, index}
      
        {#if option === "Custom Period"}
          <li
            value={option}
            on:click={customPeriodHandler}
            class:selected={option === state[key]}
            class:focused={currentIndex === index}
            on:click={(event) => customPeriodHandler(event)}
            on:focus={() => currentIndex = index}
            tabindex="0"
            id=item${index}
          >
            {$t(option)}
          </li>
        {:else if options["en-US"]}
        <li
          value={option}
          on:click={chooseOptionHandler}
          class:selected={option === state[key]}
          style="display: flex; align-items: center;"
          class:focused={currentIndex === index}
          on:focus={() => currentIndex = index}
          tabindex="0"
          id=item${index}
        >
          <div class="icon">
            <MdLanguage />
          </div>
          <div style="margin-left: 5px;">{$t(option)}</div>
        </li>
        {:else}
          <li
            value={option}
            on:click={chooseOptionHandler}
            class:selected={option === state[key]}
            class:focused={currentIndex === index}
            on:focus={() => currentIndex = index}
            tabindex="0"
            id=item${index}
          >
            {$t(option)}
          </li>
        {/if}

        {#if option === "Months" || option === "Custom Period" || option === "Sep - Nov" || option === "Nov - Apr"}
          <div>
            <div class="separator"></div>
          </div>  
        {/if}

      {/each}
    </ul>
  {/if}
</div>

<style lang="scss">
  @import "../scrollbars";

  .dropdown {
    position: relative;
    cursor: pointer;

    ul {
      position: absolute;
      z-index: 15;
      margin: 1rem 0 0 -50px;

      max-height: 192px;
      min-width: 100px;
      overflow-y: auto;
      overflow-x: hidden;

      @include scrollbars;

      display: flex;
      flex-direction: column;

      box-sizing: border-box;
      border: 1px solid var(--color-gray);
      color: var(--color-black);
      background-color: var(--color-white);

      border-radius: 0.25rem;

      list-style-type: none;
      font-size: 1rem;
      line-height: 1;
      padding: 0;

      li {
        padding: 0.5rem 1rem;
        white-space: nowrap;
        cursor: pointer;

        &:hover {
          color: var(--color-white);
          background-color: #35b472cc;
        }

        &.selected {
          color: var(--color-tertiary);
        }

        &.focused {
          background-color: #35b472cc;
          outline: none;
        }
      }

      .dropdown-title {
        font-size: small;
        color: var(--color-gray);
        text-align: center;
      }

      .separator {
        height: 1px;
        width: 100%;
        margin: 0.5rem 0;
        background-color: var(--color-gray-light);
      }
    }
  }
</style>
